import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { SalesPortalPackageRetrieve, Session } from 'types';
import { splitErrorMessage } from 'utils/error';
import { isEmpty } from 'utils/functions';

import { CheckoutStep } from './checkoutFlowStates';

export function calculateLoggedInAthleteFullName(session: Session | null): string {
  const athleteFullName = `${session?.athlete?.firstName ?? ''} ${session?.athlete?.lastName ?? ''}`.trim();
  const userFullName = `${session?.user.firstName ?? ''} ${session?.user.lastName ?? ''}`.trim();

  const fullName = !isEmpty(athleteFullName) ? athleteFullName : userFullName;
  return fullName ?? '';
}

export function calculateLoggedInAthleteEmail(session: Session | null): string {
  return session?.athlete?.email ?? session?.user.email ?? '';
}

type CheckoutPackage = Pick<SalesPortalPackageRetrieve, 'type' | 'startDate' | 'invoiceTrigger'>;

/** Whether a package bought for a selected class starts on that class's date rather than today. */
const startsOnClassDate = (type: CheckoutPackage['type']): boolean =>
  type === 'TIME_BASED' || type === 'TIME_OR_CREDIT_BASED';

/** The start date of a package bought through checkout, as an ISO string. */
export function getCheckoutStartDate(pckg: CheckoutPackage, classStartDate?: string | null): string {
  if (pckg.startDate) return pckg.startDate;
  if (classStartDate && startsOnClassDate(pckg.type)) return classStartDate;
  return dayjs().toISOString();
}

/** Whether checkout charges the first invoice now even though the package starts later. */
export function chargesFirstInvoiceImmediately(pckg: CheckoutPackage, hasClass: boolean): boolean {
  return pckg.invoiceTrigger === 'immediately' || (hasClass && startsOnClassDate(pckg.type));
}

// Error Handling Utilities
// TODO: The error handling strategy should be improved to handle errors more gracefully.

interface ErrorResponse {
  message?: string;
  step?: string;
}

type BackendStepCode =
  | 'SALES_PORTAL_GYM'
  | 'SALES_PORTAL_PACKAGE'
  | 'SALES_PORTAL_BILLING_PLAN'
  | 'ATHLETE_INFORMATION'
  | 'AGREEMENT_WAIVER'
  | 'PAYMENT_INFORMATION'
  | 'ADD_PAYMENT_INFORMATION'
  | 'PURCHASE'
  | 'KILO_CLASS_RESERVATION';

// Where an error sends the buyer in the accordion checkout:
// - step: re-open (and invalidate) the named accordion step
// - exit: the gym/package itself is invalid — leave the checkout
// - billingPlanError: the plan was disabled mid-checkout — fail forward into the
//   dedicated error screen (spec Item 4) instead of a broken form
// - kiloClassError: the class-reservation dead end for hard failures (before the
//   package start date, outside the reservation window, already booked). A class that
//   merely filled up no longer lands here — the purchase completes and the
//   confirmation renders the "class filled up" variant (spec Item 1, PR 4)
// - stay: show the error where the buyer is (Confirm purchase in the Order Summary)
export type CheckoutErrorTarget =
  | { type: 'step'; step: CheckoutStep }
  | { type: 'exit' }
  | { type: 'billingPlanError' }
  | { type: 'kiloClassError' }
  | { type: 'stay' };

export interface ParsedCheckoutError {
  messages: string[];
  target: CheckoutErrorTarget;
  isKnownError: boolean;
}

const BACKEND_STEP_CODES: BackendStepCode[] = [
  'SALES_PORTAL_GYM',
  'SALES_PORTAL_PACKAGE',
  'SALES_PORTAL_BILLING_PLAN',
  'ATHLETE_INFORMATION',
  'AGREEMENT_WAIVER',
  'PAYMENT_INFORMATION',
  'ADD_PAYMENT_INFORMATION',
  'PURCHASE',
  'KILO_CLASS_RESERVATION',
];

const BACKEND_TO_CHECKOUT_TARGET: Record<BackendStepCode, CheckoutErrorTarget> = {
  SALES_PORTAL_GYM: { type: 'exit' },
  SALES_PORTAL_PACKAGE: { type: 'exit' },
  SALES_PORTAL_BILLING_PLAN: { type: 'billingPlanError' },
  ATHLETE_INFORMATION: { type: 'step', step: 'athleteInformation' },
  AGREEMENT_WAIVER: { type: 'step', step: 'agreementWaiver' },
  PAYMENT_INFORMATION: { type: 'step', step: 'payment' },
  ADD_PAYMENT_INFORMATION: { type: 'step', step: 'payment' },
  PURCHASE: { type: 'stay' },
  KILO_CLASS_RESERVATION: { type: 'kiloClassError' },
};

function extractStepAndCleanMessages(
  errorData: ErrorResponse | undefined,
  messages: string[],
): { step: BackendStepCode | null; cleanedMessages: string[] } {
  if (errorData?.step) {
    return {
      step: errorData.step as BackendStepCode,
      cleanedMessages: messages,
    };
  }

  const stepIndex = messages.findIndex(msg => BACKEND_STEP_CODES.includes(msg as BackendStepCode));
  if (stepIndex !== -1) {
    return {
      step: messages[stepIndex] as BackendStepCode,
      cleanedMessages: messages.filter((_, index) => index !== stepIndex),
    };
  }

  return {
    step: null,
    cleanedMessages: messages,
  };
}

export function parseCheckoutError(error: AxiosError<ErrorResponse>): ParsedCheckoutError {
  const errorData = error.response?.data;
  const rawMessage = errorData?.message || 'An unexpected error occurred. Please try again.';
  const messages = splitErrorMessage(rawMessage);

  const { step, cleanedMessages } = extractStepAndCleanMessages(errorData, messages);

  const target: CheckoutErrorTarget = step ? BACKEND_TO_CHECKOUT_TARGET[step] : { type: 'stay' };

  return {
    messages: cleanedMessages.length > 0 ? cleanedMessages : [rawMessage],
    target,
    isKnownError: step !== null,
  };
}

export function handleCheckoutError(
  error: AxiosError<ErrorResponse>,
  callbacks: {
    onExitCheckout: () => void;
    onNavigateToStep: (step: CheckoutStep, message: string) => void;
    onBillingPlanError: () => void;
    onKiloClassError: (message: string) => void;
    onShowError: (message: string) => void;
  },
): void {
  const parsedError = parseCheckoutError(error);
  const errorMessage = parsedError.messages.join('\n');

  // Terminal screens carry their own explanation — no toast on top of them.
  if (parsedError.target.type !== 'kiloClassError' && parsedError.target.type !== 'billingPlanError') {
    callbacks.onShowError(errorMessage);
  }

  switch (parsedError.target.type) {
    case 'exit':
      callbacks.onExitCheckout();
      break;
    case 'step':
      callbacks.onNavigateToStep(parsedError.target.step, errorMessage);
      break;
    case 'billingPlanError':
      callbacks.onBillingPlanError();
      break;
    case 'kiloClassError':
      callbacks.onKiloClassError(errorMessage);
      break;
    case 'stay':
      break;
  }
}
